// <Icon> â€” single ingress point for Material Symbols Rounded ligatures.
//
// Per geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md "Core Rules":
//   - "One icon meaning across the entire product. No mixing icon libraries."
//   - "Standard sizes: 20px (dense rows, chips), 24px (default), 28-32px
//      (large action buttons or empty states)."
//   - "Touch target stays 40px even when the icon glyph is 20px."
//   - "Always provide accessible name. Never rely on icon alone for primary actions."
//
// Self-host policy: Material Symbols Rounded woff2 is loaded by main.tsx.
// Defence variant strips Google Fonts CDN (boss-synthesis.md Â§11.7;
// dependency-policy.md Â§1.7).

import type { CSSProperties } from 'react';
import { tokens } from '../tokens';

export type IconSize = 16 | 20 | 24 | 28 | 32;

/**
 * Registry of approved Material Symbols ligatures. The list is derived from
 * geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md and the
 * per-screen icon plans under
 * APPLICATION_PATH/design/20-design/visual-graphics-designer/icon-plan.md.
 *
 * Adding a new icon = (a) update the playbook + per-screen icon-plan, (b)
 * append the ligature here. New names without supervisor approval are a
 * supervisor reject (anti-pattern A9).
 */
export const ICON_REGISTRY = [
  // Brand / surface identity
  'widgets', 'map', 'sync_alt', 'schema', 'view_in_ar', 'terrain',
  'straighten', 'table', 'summarize', 'account_tree', 'smart_toy',
  'admin_panel_settings', 'deployed_code',
  // Navigation / shell
  'apps', 'home', 'search', 'terminal', 'notifications', 'settings', 'help',
  'account_circle', 'logout', 'business', 'chevron_right', 'arrow_back',
  'arrow_forward', 'close', 'more_vert', 'more_horiz', 'menu', 'filter_list',
  'sort', 'refresh', 'fullscreen', 'fullscreen_exit',
  // Expand / collapse chevrons — used by accordion, card, and dropdown patterns
  'expand_more', 'expand_less',
  // Object types
  'precision_manufacturing', 'timeline', 'sensors', 'valve', 'description',
  'architecture', 'folder', 'draft', 'sell', 'task_alt', 'comment',
  'attach_file',
  // Actions
  'add', 'edit', 'delete', 'save', 'check', 'send', 'check_circle', 'cancel',
  'lock', 'lock_open', 'publish', 'replay', 'restart_alt', 'play_arrow',
  'pause', 'stop', 'skip_next', 'skip_previous', 'speed', 'download',
  'upload_file', 'share', 'content_copy', 'drive_file_move', 'push_pin',
  'push_pin_off', 'bookmark', 'print', 'ios_share', 'file_download', 'link',
  'link_off', 'compare_arrows', 'clear_all',
  // Selection / editing
  'near_me', 'pan_tool', 'zoom_in', 'zoom_out', 'fit_screen',
  'center_focus_strong', 'rotate_right', 'flip', 'grid_on', 'gesture',
  'polyline', 'explore', 'crop', 'crop_free', 'content_cut', 'add_link',
  'select_all', 'deselect',
  // 2D
  'horizontal_rule', 'pentagon', 'crop_square', 'radio_button_unchecked',
  'panorama_fish_eye', 'text_fields', 'label', 'format_color_fill',
  'vertical_align_center', 'square_foot', 'layers', 'layers_clear',
  'visibility', 'visibility_off', 'document_scanner', 'grid_4x4',
  'crop_landscape',
  // 3D
  '360', 'directions_walk', 'flight', 'person_pin_circle',
  'keyboard_arrow_up', 'view_compact', 'view_sidebar', 'view_quilt',
  'filter_center_focus', 'blur_on', 'splitscreen', 'open_in_full', 'category',
  'texture', 'light_mode', 'wb_sunny', 'wb_shade', 'space_dashboard',
  'warning', 'photo_camera', 'videocam', 'tune', 'deployed_code_alert',
  'image_not_supported',
  // Map / GIS
  'location_on', 'atr', 'my_location', 'pin_drop', 'language', 'route',
  'schedule', 'directions', 'whatshot', 'crop_din', 'cloud_off',
  'sync_problem', 'wrong_location', 'public',
  // Surveying / geophysics
  'gps_fixed', 'gps_not_fixed', 'gps_off', 'flag', 'verified',
  'linear_scale', 'height', 'vertical_align_bottom', 'view_column', 'radar',
  'compass_calibration', 'scale', 'bolt', 'electric_bolt', 'graphic_eq',
  'edit_note',
  // Hydrography / marine
  'waves', 'water', 'directions_boat', 'fact_check', 'anchor',
  // Roads / transport
  'add_road', 'local_shipping', 'block', 'rule', 'construction',
  // Drawings / contours
  'landscape', 'difference', 'history',
  // Reports / workflows / tasks
  'article', 'event', 'source', 'priority_high', 'timer', 'policy',
  'rate_review',
  // Servers / infrastructure
  'dns', 'memory', 'queue', 'database', 'storage', 'api', 'rocket_launch',
  'monitor_heart',
  // AI / ML / training
  'model_training', 'dataset', 'psychology', 'trending_down', 'person_check',
  'chat',
  // AR / VR
  'vrpano', 'support_agent', 'assistant_direction', 'headset_mic',
  // Commercial / billing
  'workspace_premium', 'upgrade', 'verified_user', 'payments', 'credit_card',
  'receipt_long', 'gavel', 'monitoring', 'error', 'notifications_active',
  // Admin / tenancy
  'group', 'badge', 'key', 'vpn_key', 'manage_accounts', 'extension',
  'extension_off', 'archive',
  // Security
  'security', 'login', 'fingerprint', 'shield_lock', 'lock_reset', 'devices',
  'report',
  // Status / lifecycle
  'info', 'pending', 'open_in_new',
  // Profile / 1D
  'function', 'swap_vert',
  // Environment & Atmosphere — INIT-2026-Q3-WEATHER-ENVIRONMENT
  // Registry additions per visual-designer brief §3.3.
  // Each ligature maps 1:1 to an EnvironmentKind row.
  // Prohibited substitutes noted: use thunderstorm NOT bolt (EM-data conflict),
  // av_timer NOT schedule (isochrone conflict), water_drop NOT umbrella.
  'wb_twilight',   // sky & atmosphere (env parent card + env:sky row)
  'nights_stay',   // moon & night light (env:moon)
  'stars',         // star field / skybox (env:stars) — NOT 'star' (rating)
  'foggy',         // distance fog (env:fog)
  'wb_cloudy',     // cloud cover (env:clouds)
  'air',           // wind (env:wind)
  'water_drop',    // rain precipitation (env:rain) — NOT 'umbrella'
  'ac_unit',       // snow / cold (env:snow)
  'thunderstorm',  // lightning / weather storm (env:lightning) — NOT 'bolt' (EM data)
  'dark_mode',     // dark mode / night scene composite toggle (env:darkmode)
  'av_timer',      // time-of-day controller (env:time-of-day) — NOT 'schedule' (isochrone)
  // Misc / phase-2 additions
  'auto_awesome',
  // WP-5 (point cloud ToolCard): color-mode chip glyph for RGB â€”
  // non-color cue per ICON_SYSTEM_PLAYBOOK.md "non-color state cue" rule.
  // Added per docs/results/WP-5-pointcloud-toolcard-design-brief.md icon plan.
  'palette',
  // WP-7 â€” geophysics / hydrographic ToolCards (per
  // VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md Â§7.7 + Â§8.7).
  // `gradient` = blend / contrast affordance,
  // `query_stats` = cross-section profile placeholder while WP-9 lands the
  // chart primitive. Both are valid Material Symbols Rounded ligatures.
  'gradient', 'query_stats',
  // WP-8 â€” Sensor / live overlay ToolCard. `science` is the
  // approved scenario / simulated-data glyph per
  // ICON_SYSTEM_PLAYBOOK.md "Simulation And Animation Workspaces"
  // (Scenario: science). Paired with `bolt` (live) and `replay`
  // (replay) so the mode chip is colour AND icon distinct.
  'science',
  // WP-3 (Basemap / Raster / Terrain ToolCards). See masterplan section 8.3 +
  // per-screen icon-plan; each ligature maps 1:1 to a section 7.1-7.3 control:
  //   - 'image'         => raster ToolCard surface identity
  //   - 'flare'         => brightness slider
  //   - 'contrast'      => contrast slider
  //   - 'colorize'      => pixel-inspector pick row + saturation control
  //   - 'north'         => compass / 'reset to north' action
  //   - 'screenshot_monitor' => share / screenshot view action
  'image', 'flare', 'contrast', 'colorize',
  'north', 'screenshot_monitor',
  // Dockable panel drag handle + responsive layout collapse
  'drag_indicator', 'keyboard_arrow_down',
] as const;

export type IconName = (typeof ICON_REGISTRY)[number];

const REGISTRY_SET: ReadonlySet<string> = new Set<string>(ICON_REGISTRY);

export interface IconProps {
  /** Material Symbols ligature; must be in ICON_REGISTRY. */
  name: IconName;
  /** Visual glyph size in px. Touch target sizing is the parent's responsibility. */
  size?: IconSize;
  /** When true, decorative icon â€” `aria-hidden=true`, no SR announcement. */
  decorative?: boolean;
  /** Required when `decorative=false` and there is no visible label. */
  'aria-label'?: string;
  /** Inline style override (use sparingly; prefer wrapping primitive). */
  style?: CSSProperties;
  /** Material Symbols variant; chrome = rounded (default), in-canvas legends = outlined. */
  variant?: 'rounded' | 'outlined';
  className?: string;
  'data-testid'?: string;
}

export function Icon({
  name,
  size = 24,
  decorative = false,
  'aria-label': ariaLabel,
  style,
  variant = 'rounded',
  className,
  'data-testid': testId,
}: IconProps) {
  // Defensive runtime check â€” the TS type already prevents wrong names, but
  // a JS-only consumer or a string passed via `as any` would slip through.
  if (!REGISTRY_SET.has(name)) {
    if (typeof console !== 'undefined') {
      console.error(
        `[geosoft] <Icon name="${name}"> not in ICON_REGISTRY. ` +
          `Add to icon-plan.md + ICON_SYSTEM_PLAYBOOK.md before using. (anti-pattern A9)`,
      );
    }
  }
  const cls = ['material-symbols-' + variant, className].filter(Boolean).join(' ');
  return (
    <span
      className={cls}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : ariaLabel}
      aria-hidden={decorative ? true : undefined}
      data-testid={testId}
      style={{
        fontSize: `${size}px`,
        lineHeight: 1,
        width: `${size}px`,
        height: `${size}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Material Symbols rendering hints
        fontVariationSettings: `'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' ${size}`,
        // Ensure ligatures resolve in Firefox (Webkit/Blink default fine)
        WebkitFontFeatureSettings: '"liga"',
        fontFeatureSettings: '"liga"',
        userSelect: 'none',
        // Color is inherited; consumers set color on the wrapping element.
        color: 'currentColor',
        // Anti-aliasing for crisp glyph
        WebkitFontSmoothing: 'antialiased',
        // Force Tailwind/global resets to not break Material Symbols
        verticalAlign: 'middle',
        // The token system has no raw px â€” but the size prop is itself a
        // bounded literal type IconSize from the token-aligned scale.
        flex: '0 0 auto',
        // Subtle: Material Symbols requires the font face declared at root.
        // See main.tsx font face injection.
        ...(style ?? {}),
      }}
    >
      {name}
    </span>
  );
}

// Token-tied helper for consumers who need the icon-glyph dimension
// independent of the touch target (e.g. composing into a 40 px IconButton).
export function iconGlyphSize(size: IconSize): number {
  // Identity today; reserved as the seam for a future density-aware scale.
  void tokens; // satisfy linter; intentional dependency keep
  return size;
}
